import React from 'react';
import * as ort from 'onnxruntime-web/webgpu';
import { db } from './database';

// Configure ONNX Runtime environment
ort.env.wasm.wasmPaths = process.env.PUBLIC_URL + '/wasm/';
ort.env.webgpu.device = null; // Will be set during initialization

interface ModelConfig {
  modelPath: string;
  tokenizerPath: string;
  executionProvider: 'webgpu' | 'wasm';
  quantization: 'int4' | 'int8' | 'fp16';
  maxTokens: number;
  cacheInGPU: boolean;
}

interface InferenceResult {
  text: string;
  confidence: number;
  processingTime: number;
  tokensGenerated: number;
  executionProvider: string;
}

class ModelService {
  private session: ort.InferenceSession | null = null;
  private tokenizer: any = null;
  private config: ModelConfig = {
    modelPath: '/models/gemma-3n-e2b/model_quantized.onnx',
    tokenizerPath: '/models/gemma-3n-e2b/tokenizer.json',
    executionProvider: 'wasm',
    quantization: 'int4',
    maxTokens: 2048,
    cacheInGPU: true,
  };
  
  private gpuDevice: any | null = null; // GPUDevice type not available in all environments
  private isInitialized = false;
  private initPromise: Promise<void> | null = null;
  
  async initialize(config?: Partial<ModelConfig>): Promise<void> {
    if (this.isInitialized) return;
    
    // Prevent multiple initialization calls
    if (this.initPromise) {
      return this.initPromise;
    }
    
    this.initPromise = this._initialize(config);
    await this.initPromise;
    this.initPromise = null;
  }
  
  private async _initialize(config?: Partial<ModelConfig>): Promise<void> {
    try {
      if (config) {
        this.config = { ...this.config, ...config };
      }
      
      // Detect WebGPU availability
      const webgpuInfo = await this.detectWebGPU();
      
      if (webgpuInfo.available) {
        this.config.executionProvider = 'webgpu';
        this.gpuDevice = webgpuInfo.device!;
        ort.env.webgpu.device = this.gpuDevice;
        
        console.log('WebGPU initialized:', {
          adapter: webgpuInfo.adapterInfo,
          features: Array.from(webgpuInfo.features || []),
          limits: webgpuInfo.limits,
        });
      } else {
        console.warn('WebGPU not available, falling back to WASM');
        this.config.executionProvider = 'wasm';
      }
      
      // Load tokenizer
      try {
        await this.loadTokenizer();
      } catch (error) {
        console.warn('Tokenizer not available - using placeholder');
        this.tokenizer = { type: 'placeholder' };
      }
      
      // Create inference session
      try {
        await this.createSession();
      } catch (error) {
        console.warn('Model not available - AI features disabled');
        // Don't throw - allow app to work without model
      }
      
      this.isInitialized = true;
      
      // Track initialization in analytics
      await db.trackAnalytics({
        type: 'performance',
        data: {
          event: 'model_initialized',
          executionProvider: this.config.executionProvider,
          quantization: this.config.quantization,
          webgpuAvailable: webgpuInfo.available,
        },
      });
      
    } catch (error) {
      console.error('Model initialization failed:', error);
      throw new Error(`Failed to initialize model: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
  
  private async detectWebGPU(): Promise<{
    available: boolean;
    device?: any; // GPUDevice
    adapterInfo?: any; // GPUAdapterInfo
    features?: any; // GPUSupportedFeatures
    limits?: any; // GPUSupportedLimits
  }> {
    if (!('gpu' in navigator)) {
      return { available: false };
    }
    
    try {
      const adapter = await (navigator as any).gpu.requestAdapter({
        powerPreference: 'high-performance',
        forceFallbackAdapter: false,
      });
      
      if (!adapter) {
        return { available: false };
      }
      
      // Check for required features
      const requiredFeatures: any[] = []; // GPUFeatureName[]
      if (adapter.features.has('shader-f16')) {
        requiredFeatures.push('shader-f16');
      }
      
      const device = await adapter.requestDevice({
        requiredFeatures,
        requiredLimits: {
          maxBufferSize: adapter.limits.maxBufferSize,
          maxStorageBufferBindingSize: adapter.limits.maxStorageBufferBindingSize,
          maxComputeWorkgroupStorageSize: adapter.limits.maxComputeWorkgroupStorageSize,
        },
      });
      
      // requestAdapterInfo may not be available in all browsers
      let adapterInfo = null;
      if (adapter.requestAdapterInfo) {
        adapterInfo = await adapter.requestAdapterInfo();
      }
      
      return {
        available: true,
        device,
        adapterInfo,
        features: adapter.features,
        limits: adapter.limits,
      };
      
    } catch (error) {
      console.error('WebGPU detection failed:', error);
      return { available: false };
    }
  }
  
  private async loadTokenizer(): Promise<void> {
    try {
      const response = await fetch(this.config.tokenizerPath);
      if (!response.ok) {
        throw new Error(`Failed to load tokenizer: ${response.status}`);
      }
      
      this.tokenizer = await response.json();
      console.log('Tokenizer loaded successfully');
      
    } catch (error) {
      console.error('Tokenizer loading failed:', error);
      throw error;
    }
  }
  
  private async createSession(): Promise<void> {
    try {
      const modelResponse = await fetch(this.config.modelPath);
      if (!modelResponse.ok) {
        throw new Error(`Failed to load model: ${modelResponse.status}`);
      }
      
      const modelBuffer = await modelResponse.arrayBuffer();
      
      // Check if it's a placeholder
      const textDecoder = new TextDecoder();
      const bufferStart = textDecoder.decode(modelBuffer.slice(0, 20));
      if (bufferStart.includes('PLACEHOLDER')) {
        console.warn('[ModelService] Placeholder model detected - AI features disabled');
        return;
      }
      
      // Session options
      const sessionOptions: ort.InferenceSession.SessionOptions = {
        executionProviders: [this.config.executionProvider],
        graphOptimizationLevel: 'all',
        enableCpuMemArena: true,
        enableMemPattern: true,
        executionMode: 'sequential',
        interOpNumThreads: 1,
        intraOpNumThreads: 0, // Use default
        logId: 'gemma-3n',
        logSeverityLevel: 2, // Warning
        logVerbosityLevel: 0,
        optimizedModelFilePath: undefined,
        enableProfiling: false,
        profileFilePrefix: 'gemma-profile',
      };
      
      // WebGPU-specific options
      if (this.config.executionProvider === 'webgpu') {
        sessionOptions.preferredOutputLocation = this.config.cacheInGPU ? 'gpu-buffer' : 'cpu';
      }
      
      this.session = await ort.InferenceSession.create(
        new Uint8Array(modelBuffer),
        sessionOptions
      );
      
      console.log('Model session created:', {
        inputNames: this.session.inputNames,
        outputNames: this.session.outputNames,
      });
      
    } catch (error) {
      console.error('Session creation failed:', error);
      throw error;
    }
  }
  
  getStatus() {
    return {
      initialized: this.isInitialized,
      hasWebGPU: this.config.executionProvider === 'webgpu',
      modelPath: this.config.modelPath,
    };
  }
  
  async processImage(
    imageData: ImageData | ArrayBuffer,
    prompt: string,
    options?: {
      temperature?: number;
      topK?: number;
      topP?: number;
      maxTokens?: number;
    }
  ): Promise<InferenceResult> {
    if (!this.isInitialized || !this.session) {
      await this.initialize();
    }
    
    const startTime = performance.now();
    
    try {
      // Preprocess image
      const pixelValues = await this.preprocessImage(imageData);
      
      // Tokenize prompt
      const inputIds = await this.tokenizeText(prompt);
      
      // Prepare inputs
      const feeds: Record<string, ort.Tensor> = {
        pixel_values: new ort.Tensor('float32', pixelValues.data, pixelValues.dims),
        input_ids: new ort.Tensor('int64', inputIds.data, inputIds.dims),
      };
      
      // Add optional inputs if the model supports them
      if (this.session?.inputNames.includes('temperature') && options?.temperature) {
        feeds.temperature = new ort.Tensor('float32', [options.temperature], [1]);
      }
      
      // Run inference
      let output: ort.InferenceSession.OnnxValueMapType;
      
      // Standard inference (IOBinding not yet supported in ONNX Runtime Web)
      output = await this.session!.run(feeds);
      
      // Process output
      const result = await this.processOutput(output);
      
      const processingTime = performance.now() - startTime;
      
      // Clean up tensors
      for (const tensor of Object.values(feeds)) {
        tensor.dispose();
      }
      
      for (const tensor of Object.values(output)) {
        if (tensor instanceof ort.Tensor) {
          tensor.dispose();
        }
      }
      
      return {
        text: result.text,
        confidence: result.confidence,
        processingTime,
        tokensGenerated: result.tokens,
        executionProvider: this.config.executionProvider,
      };
      
    } catch (error) {
      console.error('Inference failed:', error);
      throw new Error(`Inference failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
  
  private async preprocessImage(
    imageData: ImageData | ArrayBuffer
  ): Promise<{ data: Float32Array; dims: number[] }> {
    let pixels: Uint8ClampedArray;
    let width: number;
    let height: number;
    
    if (imageData instanceof ImageData) {
      pixels = imageData.data;
      width = imageData.width;
      height = imageData.height;
    } else {
      // Convert ArrayBuffer to ImageData
      const blob = new Blob([imageData]);
      const bitmap = await createImageBitmap(blob);
      const canvas = new OffscreenCanvas(bitmap.width, bitmap.height);
      const ctx = canvas.getContext('2d') as OffscreenCanvasRenderingContext2D;
      ctx.drawImage(bitmap, 0, 0);
      const imgData = ctx.getImageData(0, 0, bitmap.width, bitmap.height);
      pixels = imgData.data;
      width = imgData.width;
      height = imgData.height;
    }
    
    // Resize to model input size (e.g., 256x256)
    const targetSize = 256;
    const canvas = new OffscreenCanvas(targetSize, targetSize);
    const ctx = canvas.getContext('2d') as OffscreenCanvasRenderingContext2D;
    
    // Create ImageData from pixels
    const sourceImageData = new ImageData(pixels, width, height);
    const tempCanvas = new OffscreenCanvas(width, height);
    const tempCtx = tempCanvas.getContext('2d') as OffscreenCanvasRenderingContext2D;
    tempCtx.putImageData(sourceImageData, 0, 0);
    
    // Draw resized
    ctx.drawImage(tempCanvas, 0, 0, targetSize, targetSize);
    const resizedData = ctx.getImageData(0, 0, targetSize, targetSize);
    
    // Convert to normalized float32 array (RGB channels)
    const float32Data = new Float32Array(3 * targetSize * targetSize);
    
    for (let i = 0; i < resizedData.data.length; i += 4) {
      const pixelIndex = i / 4;
      const channelOffset = targetSize * targetSize;
      
      // Normalize to [-1, 1] or [0, 1] depending on model requirements
      float32Data[pixelIndex] = resizedData.data[i] / 255.0;                    // R
      float32Data[pixelIndex + channelOffset] = resizedData.data[i + 1] / 255.0; // G
      float32Data[pixelIndex + 2 * channelOffset] = resizedData.data[i + 2] / 255.0; // B
    }
    
    return {
      data: float32Data,
      dims: [1, 3, targetSize, targetSize], // NCHW format
    };
  }
  
  private async tokenizeText(text: string): Promise<{ data: BigInt64Array; dims: number[] }> {
    if (!this.tokenizer) {
      throw new Error('Tokenizer not loaded');
    }
    
    // Simple tokenization - in production, use proper tokenizer
    const tokens: bigint[] = [];
    
    // Add special tokens
    if (this.tokenizer.bos_token_id) {
      tokens.push(BigInt(this.tokenizer.bos_token_id));
    }
    
    // Tokenize text (simplified - real implementation would use proper tokenization)
    const words = text.toLowerCase().split(/\s+/);
    for (const word of words) {
      const tokenId = this.tokenizer.vocab[word] || this.tokenizer.unk_token_id || 0;
      tokens.push(BigInt(tokenId));
    }
    
    // Pad or truncate to max length
    const maxLength = 512;
    while (tokens.length < maxLength) {
      tokens.push(BigInt(this.tokenizer.pad_token_id || 0));
    }
    
    if (tokens.length > maxLength) {
      tokens.splice(maxLength);
    }
    
    return {
      data: new BigInt64Array(tokens),
      dims: [1, tokens.length],
    };
  }
  
  private async processOutput(output: ort.InferenceSession.OnnxValueMapType): Promise<{
    text: string;
    confidence: number;
    tokens: number;
  }> {
    // Get the output tensor
    const outputTensor = output[this.session!.outputNames[0]];
    
    if (!(outputTensor instanceof ort.Tensor)) {
      throw new Error('Invalid output format');
    }
    
    // Process logits to text (simplified)
    const logits = outputTensor.data as Float32Array;
    const vocabSize = this.tokenizer?.vocab_size || 32000;
    
    // Generate tokens (simplified greedy decoding)
    const generatedTokens: number[] = [];
    let totalConfidence = 0;
    
    for (let i = 0; i < logits.length; i += vocabSize) {
      const tokenLogits = logits.slice(i, i + vocabSize);
      const maxIndex = tokenLogits.indexOf(Math.max(...Array.from(tokenLogits)));
      generatedTokens.push(maxIndex);
      
      // Calculate confidence (softmax of max logit)
      const maxLogit = tokenLogits[maxIndex];
      const expSum = Array.from(tokenLogits).reduce((sum, logit) => sum + Math.exp(logit - maxLogit), 0);
      const confidence = Math.exp(0) / expSum;
      totalConfidence += confidence;
    }
    
    // Decode tokens to text (simplified)
    const text = this.decodeTokens(generatedTokens);
    
    return {
      text,
      confidence: totalConfidence / generatedTokens.length,
      tokens: generatedTokens.length,
    };
  }
  
  private decodeTokens(tokens: number[]): string {
    if (!this.tokenizer) {
      return 'Tokenizer not available';
    }
    
    // Reverse vocabulary lookup (simplified)
    const reverseVocab: Record<number, string> = {};
    for (const [word, id] of Object.entries(this.tokenizer.vocab)) {
      reverseVocab[id as any] = word;
    }
    
    const words = tokens
      .map(id => reverseVocab[id] || '<unk>')
      .filter(word => word !== '<pad>' && word !== '<eos>');
    
    return words.join(' ');
  }
  
  async warmup(): Promise<void> {
    if (!this.isInitialized) {
      await this.initialize();
    }
    
    console.log('Warming up model...');
    
    // Create dummy inputs
    const dummyImage = new ImageData(256, 256);
    const dummyPrompt = 'Extract text from this image';
    
    try {
      await this.processImage(dummyImage, dummyPrompt, { maxTokens: 10 });
      console.log('Model warmup complete');
    } catch (error) {
      console.warn('Model warmup failed:', error);
    }
  }
  
  getModelInfo(): {
    initialized: boolean;
    executionProvider: string;
    quantization: string;
    inputShapes?: Record<string, number[]>;
    outputShapes?: Record<string, number[]>;
  } {
    const info: any = {
      initialized: this.isInitialized,
      executionProvider: this.config.executionProvider,
      quantization: this.config.quantization,
    };
    
    if (this.session) {
      info.inputShapes = {};
      info.outputShapes = {};
      
      // Get input/output shapes from session
      // Note: ONNX Runtime Web doesn't expose shapes directly,
      // so these would need to be hardcoded based on model knowledge
      info.inputShapes = {
        pixel_values: [1, 3, 256, 256],
        input_ids: [1, 512],
      };
      
      info.outputShapes = {
        output: [1, 512, 32000], // sequence_length x vocab_size
      };
    }
    
    return info;
  }
  
  async dispose(): Promise<void> {
    if (this.session) {
      await this.session.release();
      this.session = null;
    }
    
    this.tokenizer = null;
    this.isInitialized = false;
    
    if (this.gpuDevice) {
      this.gpuDevice.destroy();
      this.gpuDevice = null;
    }
  }
}

// Export singleton instance
export const modelService = new ModelService();

// React hook for model status
export function useModelStatus() {
  const [status, setStatus] = React.useState<ReturnType<typeof modelService.getModelInfo>>();
  
  React.useEffect(() => {
    // Get initial status
    setStatus(modelService.getModelInfo());
    
    // Initialize model
    modelService.initialize().then(() => {
      setStatus(modelService.getModelInfo());
    }).catch(console.error);
    
    return () => {
      // Cleanup if needed
    };
  }, []);
  
  return status;
}